import { useSnackbar } from "../../../components/minimal/snackbar";
import { useLocales } from "../../../locales";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { Box, Button, Card, IconButton, Stack, Typography } from "@mui/material";
import FormProvider, { RHFPhoneInput } from "../../../components/minimal/hook-form";
import Iconify from "../../../components/minimal/iconify";
import { useEffect } from "react";
import { m } from "framer-motion";
import parsePhoneNumber from "libphonenumber-js";

export const SmsCampaignManuallyForm = ({ setSmsCampaignPhoneNumbersManually, smsCampaignPhoneNumbersManually }) => {

    const { enqueueSnackbar } = useSnackbar()
    const { translate } = useLocales()


    const methods = useForm({ resolver: yupResolver(), smsCampaignPhoneNumbersManually });
    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: "phoneNumbers"
    });

    useEffect(() => {
        if (smsCampaignPhoneNumbersManually && smsCampaignPhoneNumbersManually.length) {
            smsCampaignPhoneNumbersManually.forEach(number => {
                append(number);
            });
        }
    }, []);

    // Validates every entered number with libphonenumber-js (not an Israeli-only
    // regex) so the form works for any country selected in the dropdown, and sets
    // field-level + snackbar errors. Returns true when all numbers are valid.
    const validatePhoneNumbers = (phoneNumbers) => {
        const parsedNumbers = phoneNumbers.map(num => parsePhoneNumber((num || '').toString().trim()));

        // Empty / incomplete fields (nothing typed beyond the country code).
        const emptyIndex = parsedNumbers.findIndex(phone => !phone?.nationalNumber);
        if (emptyIndex !== -1) {
            methods.setError(`phoneNumbers[${emptyIndex}]`, { message: translate('sms.fill_field') });
            enqueueSnackbar(translate('dashboard.sms_campaign.sms_alert_please_provide_all_phone_numbers'), { variant: 'error' });
            return false;
        }

        // Invalid numbers (e.g. wrong length for the chosen country).
        const invalidIndex = parsedNumbers.findIndex(phone => !phone.isValid());
        if (invalidIndex !== -1) {
            methods.setError(`phoneNumbers[${invalidIndex}]`, { message: translate('sms.invalid_phone_number') });
            enqueueSnackbar(translate('dashboard.sms_campaign.sms_alert_invalid_phone_numbers_provided'), { variant: 'error' });
            return false;
        }

        // Duplicates, compared on the normalized E.164 value so that, for example,
        // 050-123-4567 and +972501234567 are treated as the same number.
        const seen = new Map();
        for (let i = 0; i < parsedNumbers.length; i++) {
            const e164 = parsedNumbers[i].number;
            if (seen.has(e164)) {
                methods.setError(`phoneNumbers[${seen.get(e164)}]`, { message: translate('sms.duplicate_numbers') });
                methods.setError(`phoneNumbers[${i}]`, { message: translate('sms.duplicate_numbers') });
                enqueueSnackbar(translate('dashboard.sms_campaign.sms_alert_duplicate_phone_numbers_are_not_allowed'), { variant: 'error' });
                return false;
            }
            seen.set(e164, i);
        }

        return true;
    };

    const handleAppend = () => {
        const { phoneNumbers = [] } = methods.getValues();

        // First time click on a new number — just add an empty input to fill in.
        if (!phoneNumbers.length) {
            append('');
            return;
        }

        if (!validatePhoneNumbers(phoneNumbers)) return;

        append(''); // Append new phone number if all existing ones are valid
    };

    const approvePhoneNumbers = () => {
        const { phoneNumbers = [] } = methods.getValues();
        if (!validatePhoneNumbers(phoneNumbers)) return;

        // Store normalized E.164 numbers so the manual path matches the CSV path and
        // dedupes correctly against the other sources in the parent component.
        const normalized = phoneNumbers.map(num => parsePhoneNumber(num.toString().trim()).number);
        setSmsCampaignPhoneNumbersManually([...new Set(normalized)]);
    }

    function removeSelectedNumbers(numberToRemove) {
        const filteredNumbers = smsCampaignPhoneNumbersManually.filter(num => num !== numberToRemove)
        setSmsCampaignPhoneNumbersManually(filteredNumbers)
    }

    useEffect(() => {
        methods.clearErrors()
    }, [methods.watch()])

    return (
        <Card sx={{ p: 3, marginBottom: 2, minWidth: '300px' }}>
            <FormProvider width="100%" methods={methods}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    {translate('dashboard.sms_campaign.upload_phones_numbers_manually')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {fields.map((item, index) => (
                        <m.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }} // Initial state
                            animate={{ opacity: 1, y: 0 }}   // Animation state
                            exit={{ opacity: 0, y: -20 }}    // Exit state
                            transition={{ duration: 0.3 }}   // Transition duration
                        >
                            <Stack sx={{ py: 1.6, display: "flex", flexDirection: "row", alignItems: "center" }} key={item.id}>
                                <RHFPhoneInput
                                    size="small"
                                    name={`phoneNumbers[${index}]`}
                                    label={`${translate('sms.phone_number')} ${index + 1}`}
                                />
                                <IconButton
                                    sx={{
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        alignSelf: "center",
                                        marginLeft: "8px"
                                    }}
                                    onClick={() => {
                                        const raw = methods.getValues(`phoneNumbers[${index}]`)
                                        const phone = parsePhoneNumber((raw || '').toString().trim())
                                        const currentNumber = phone && phone.isValid() ? phone.number : raw
                                        // Remove the originally approved value for this row, not the
                                        // (possibly edited, not-yet-approved) input value.
                                        const numberToRemove = smsCampaignPhoneNumbersManually?.[index] ?? currentNumber
                                        removeSelectedNumbers(numberToRemove)
                                        remove(index)
                                    }
                                    }>
                                    <Iconify sx={{ width: "20px", height: "20px" }} icon="mdi:delete" />
                                </IconButton>
                            </Stack>
                        </m.div>
                    ))}

                </Box>
                <Stack sx={{ gap: 2, mt: '35px' }}>
                    <Button onClick={handleAppend}>
                        <Iconify icon="eva:plus-fill" />
                        {translate('sms.add_number')}
                    </Button>
                    <Button disabled={!fields.length} onClick={approvePhoneNumbers} variant="contained">
                        {translate('sms.approve')}
                    </Button>
                </Stack>
            </FormProvider>
        </Card>
    )
}
